<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { getContext } from "svelte";
  import { TRANSITION_DURATION } from "$lib/utils/config.js";

  const { data, xScale, yScale, yRange, xGet } = getContext("LayerCake");

  const STROKE_WIDTH = 2;

  let {
    groups = [],
    colors = [],
    overlapFraction = 0.25,
    separatorEffect = "none",
    strokeColor = null,
    liftOnHover = false,
    activeGroup = undefined,
    activeIndex = undefined,
    activeEl = $bindable(null),
    maxBarWidth = 60,
    onhover = () => {},
    onleave = () => {},
  } = $props();

  let rawBarWidth = $derived(
    $xScale.bandwidth() / (1 + (groups.length - 1) * (1 - overlapFraction)) || 0
  );
  let barWidth = $derived(Math.min(rawBarWidth, maxBarWidth));
  let stepOffset = $derived(barWidth * (1 - overlapFraction) || 0);
  let groupWidth = $derived(barWidth + (groups.length - 1) * stepOffset);
  let bandwidthOffset = $derived(($xScale.bandwidth() - groupWidth) / 2);
</script>

<defs>
  <filter id="bar-shadow" x="-10%" y="-10%" width="120%" height="120%">
    <feDropShadow dx="-1" dy="0" stdDeviation="1" flood-color="black" flood-opacity="0.12" />
  </filter>
  <clipPath id="clip-bottom-stroke">
    <rect x="-9999" y="-9999" width="99999" height={$yRange[0] + 9999 - STROKE_WIDTH} />
  </clipPath>
</defs>
<g class="grouped-column-group" style="--transition-duration: {TRANSITION_DURATION}ms">
  {#each $data as d, di}
    {@const xPos = $xGet(d)}
    {@const x0 = Array.isArray(xPos) ? xPos[0] : xPos}
    {#each groups as group, gi}
      {@const val = d[group]}
      {#if val}
        {@const yPos = $yScale(val)}
        {@const barHeight = $yRange[0] - yPos}
        {@const bx = x0 + bandwidthOffset + gi * stepOffset}
        {@const bh = Math.max(0, barHeight)}
        <rect
          class="bar"
          x={bx}
          y={yPos}
          width={barWidth}
          height={bh}
          fill={colors[gi]}
          opacity={activeIndex != null ? (activeIndex === di && activeGroup === gi ? 1 : 0.4) : 1}
          filter={separatorEffect === "shadow" && gi > 0 ? "url(#bar-shadow)" : undefined}
          onmousemove={(e) => { activeEl = e.currentTarget; onhover(e, d, group, gi, di); }}
          onmouseleave={() => { activeEl = null; onleave(); }}
          role="img"
        />
        {#if separatorEffect === "stroke" && strokeColor}
          <rect
            class="bar"
            x={bx}
            y={yPos}
            width={barWidth}
            height={bh}
            fill="none"
            stroke={strokeColor}
            stroke-width={STROKE_WIDTH}
            clip-path="url(#clip-bottom-stroke)"
            opacity={activeIndex != null ? (activeIndex === di && activeGroup === gi ? 1 : 0.4) : 1}
            style="pointer-events: none;"
          />
        {/if}
      {:else}
        {@const bx = x0 + bandwidthOffset + gi * stepOffset}
        {@const nullHeight = $yRange[0] * 0.4}
        {@const nullY = $yRange[0] - nullHeight}
        <rect
          x={bx}
          y={nullY}
          width={barWidth}
          height={nullHeight}
          fill="transparent"
          onmousemove={(e) => { activeEl = e.currentTarget; onhover(e, d, group, gi, di); }}
          onmouseleave={() => { activeEl = null; onleave(); }}
          role="img"
        />
      {/if}
    {/each}
  {/each}
</g>
{#if liftOnHover && activeIndex != null && activeGroup != null}
  {@const d = $data[activeIndex]}
  {@const xPos = $xGet(d)}
  {@const x0 = Array.isArray(xPos) ? xPos[0] : xPos}
  {@const group = groups[activeGroup]}
  {@const val = d[group]}
  {#if val != null}
    {@const yPos = $yScale(val)}
    {@const barHeight = $yRange[0] - yPos}
    {@const bx = x0 + bandwidthOffset + activeGroup * stepOffset}
    {@const bh = Math.max(0, barHeight)}
    <g style="pointer-events: none;">
      <rect
        x={bx}
        y={yPos}
        width={barWidth}
        height={bh}
        fill={colors[activeGroup]}
      />
      {#if strokeColor}
        <rect
          x={bx}
          y={yPos}
          width={barWidth}
          height={bh}
          fill="none"
          stroke={strokeColor}
          stroke-width={STROKE_WIDTH}
          clip-path="url(#clip-bottom-stroke)"
        />
      {/if}
    </g>
  {/if}
{/if}

<style>
  .bar {
    transition: y var(--transition-duration) ease, height var(--transition-duration) ease, opacity 0.2s ease;
  }
</style>
